import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'

import { StatusPill } from '@/components/px/StatusPill'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'
import { boardViewSearch } from '../boardView'
import { BOARD_FOCUS_RING } from '../focusRing'
import { selectEpicSummaries } from '../slices/boardSlice'
import { UNASSIGNED_EPIC_KEY, type EpicSummary } from '../types'

/**
 * The Build board's default view (CR-BUILD-BOARD-EPICS-2026-09-26, AC-16):
 * one small card per epic, in `GET …/epics` order, then the Unassigned card
 * when it counts anything. Every figure comes from `selectEpicSummaries`
 * (INV-R.2); this component computes nothing. A card is one whole `<Link>`
 * to the Tasks view filtered to its epic.
 */
export function EpicsView() {
  const summaries = useAppSelector(selectEpicSummaries)
  return (
    <ul data-testid="epic-cards" className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-3">
      {summaries.map((summary) => (
        <li key={summary.key}>
          <EpicCard summary={summary} />
        </li>
      ))}
    </ul>
  )
}

interface EpicCardProps {
  summary: EpicSummary
}

function EpicCard({ summary }: EpicCardProps) {
  const { t } = useTranslation('projectWorkspace')
  const [params] = useSearchParams()
  const { key, total, done, buildQueue, awaitingReview, percent } = summary
  const unassigned = key === UNASSIGNED_EPIC_KEY
  const title = unassigned ? t('board.epics.unassigned') : (summary.title ?? key)
  const to = useMemo(
    () => ({
      search: boardViewSearch(params, {
        view: 'tasks',
        epicFilter: unassigned ? { kind: 'unassigned' } : { kind: 'epic', id: key }
      })
    }),
    [params, unassigned, key]
  )

  return (
    <Link
      to={to}
      data-testid={`epic-card-${key}`}
      className={cn(
        'group flex h-full flex-col gap-2.5 rounded-lg border border-border bg-card px-4 py-3.5 hover:border-muted-foreground',
        unassigned && 'border-dashed',
        BOARD_FOCUS_RING
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {unassigned ? (
            <span className="text-[0.7rem] font-semibold text-muted-foreground">{t('board.epics.unassignedHint')}</span>
          ) : (
            <span className="font-mono text-[0.7rem] font-semibold tracking-[0.06em] text-muted-foreground">{key}</span>
          )}
          <span
            className={cn(
              'mt-0.5 block text-sm font-semibold leading-snug [overflow-wrap:anywhere] group-hover:underline group-hover:underline-offset-2',
              unassigned ? 'text-ink-soft' : 'text-foreground'
            )}
          >
            {title}
          </span>
        </div>
        <ChevronRight aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
      </div>
      <div className="mt-auto flex flex-col gap-2">
        <span className="v-figure text-sm text-foreground">{t('board.epics.doneOfTotal', { done, total })}</span>
        {/* The installed shadcn `Progress` keeps `value` for the indicator and never hands it to the Radix
            root (upstream shape), so the root's `aria-valuenow` is passed explicitly (AC-16). */}
        <Progress
          value={percent}
          aria-valuenow={percent}
          aria-label={t('board.epics.progressLabel', { title, done, total })}
        />
        <div className="flex min-h-5 flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
          <span
            data-testid={`epic-card-queue-${key}`}
            className={buildQueue > 0 ? 'text-ink-soft' : 'text-muted-foreground'}
          >
            {t('board.epics.inBuildQueue', { count: buildQueue })}
          </span>
          {awaitingReview > 0 ? (
            <StatusPill tone="review">
              <span data-testid={`epic-card-review-${key}`}>{t('board.epics.awaitingReview', { count: awaitingReview })}</span>
            </StatusPill>
          ) : (
            <span data-testid={`epic-card-review-${key}`} className="text-muted-foreground">
              {t('board.epics.awaitingReview', { count: awaitingReview })}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}
